import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { useSettings } from "../context/SettingsContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Banknote, Upload, ShieldCheck, Truck, LogIn, Tag, X, Gift, Wallet, CreditCard } from "lucide-react";

const COUNTRIES = ["Jamaica", "United States", "Canada", "United Kingdom", "Trinidad and Tobago", "Barbados", "Bahamas", "Cayman Islands", "Other"];

export default function Checkout() {
  const nav = useNavigate();
  const { items, subtotal, clearCart } = useCart();
  const { user, loading, login } = useAuth();
  const { format, currency, exchangeRate } = useCurrency();
  const { t } = useSettings();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    customer_name: "", email: "", phone: "", country: "Jamaica",
    shipping_address: "", shipping_option: "Local Pickup", payment_method: "bank_transfer",
    notes: "",
  });
  const [proof, setProof] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [agreedRefund, setAgreedRefund] = useState(false);
  const [wipayProcessing, setWipayProcessing] = useState(false);

  // Promo code
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);

  // Voucher
  const [voucherInput, setVoucherInput] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [voucherBusy, setVoucherBusy] = useState(false);

  // Store credit
  const [storeCredit, setStoreCredit] = useState({ amount: 0, currency: "USD" });

  // Dynamic shipping
  const [shippingSubOption, setShippingSubOption] = useState("");
  const [shippingFields, setShippingFields] = useState({});

  // Re-poll settings so bulk discount / shipping changes appear live without reload
  useEffect(() => {
    const id = setInterval(() => { api.get("/settings").then(r => setSettings(r.data)).catch(() => {}); }, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    document.title = "Checkout – Cheap4Teach";
    api.get("/settings").then(r => {
      setSettings(r.data);
      if (r.data.shipping_options?.length) {
        const first = r.data.shipping_options[0];
        setForm(f => ({ ...f, shipping_option: first.name }));
        if (first.sub_options?.length) setShippingSubOption(first.sub_options[0].location);
      }
    });
    if (!loading && items.length === 0) nav("/cart");
  }, [items.length, nav, loading]);

  useEffect(() => {
    if (user) {
      setForm(f => ({ ...f, customer_name: f.customer_name || user.name, email: f.email || user.email }));
      api.get("/users/me/credit").then(r => setStoreCredit({ amount: r.data.store_credit_amount || 0, currency: r.data.store_credit_currency || "USD" })).catch(() => {});
    }
  }, [user]);

  const shippingOptions = settings?.shipping_options || [];
  const selectedOpt = useMemo(
    () => shippingOptions.find(o => o.name === form.shipping_option) || { price: 0, sub_options: [], fields: [] },
    [shippingOptions, form.shipping_option]
  );

  const changeShippingOption = (name) => {
    const opt = shippingOptions.find(o => o.name === name) || {};
    setForm(f => ({ ...f, shipping_option: name }));
    setShippingSubOption(opt.sub_options?.length ? opt.sub_options[0].location : "");
    setShippingFields({});
  };

  // Shipping prices are stored in JMD directly (JMD-first).
  const shipping_cost = useMemo(() => {
    if (selectedOpt.sub_options?.length && shippingSubOption) {
      const sub = selectedOpt.sub_options.find(s => s.location === shippingSubOption);
      return Number(sub?.price || 0);
    }
    return Number(selectedOpt.price || 0);
  }, [selectedOpt, shippingSubOption]);

  const shippingFieldsValid = useMemo(() => {
    if (!selectedOpt.fields?.length) return true;
    return selectedOpt.fields.every(f => !f.required || (shippingFields[f.label] || "").trim());
  }, [selectedOpt.fields, shippingFields]);

  const promoDiscount = promo ? +((subtotal * promo.percent_off) / 100).toFixed(2) : 0;
  const bulkQty = items.reduce((sum, it) => sum + (it.quantity || 0), 0);
  const bulkActive = !promo && settings && settings.bulk_discount_enabled && (settings.bulk_discount_percent || 0) > 0 && bulkQty >= (settings.bulk_discount_min_items || 0) && (settings.bulk_discount_min_items || 0) > 0;
  const bulkDiscount = bulkActive ? +((subtotal * settings.bulk_discount_percent) / 100).toFixed(2) : 0;
  const discountedSubtotal = Math.max(0, subtotal - promoDiscount - bulkDiscount);
  const afterShipping = discountedSubtotal + shipping_cost;
  // voucherBalance and creditToUse are in JMD (same as all other amounts)
  const voucherBalanceJMD = voucher
    ? (voucher.currency === "JMD"
        ? voucher.balance
        : Math.round(voucher.balance * (exchangeRate || 160)))
    : 0;
  const voucherDiscount = voucher ? Math.min(voucherBalanceJMD, afterShipping) : 0;
  const afterVoucher = Math.max(0, afterShipping - voucherDiscount);
  const creditJMD = storeCredit.amount > 0
    ? (storeCredit.currency === "JMD"
        ? storeCredit.amount
        : Math.round(storeCredit.amount * (exchangeRate || 160)))
    : 0;
  const creditToUse = creditJMD > 0 ? Math.min(creditJMD, afterVoucher) : 0;
  const creditRemainingJMD = creditJMD - creditToUse;
  const total = Math.max(0, afterVoucher - creditToUse); // keep full USD precision; format() rounds for display

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoBusy(true);
    try {
      const r = await api.post("/promo-codes/validate", { code });
      setPromo({ code: r.data.code, percent_off: r.data.percent_off });
      toast.success(`Promo "${r.data.code}" applied. ${r.data.percent_off}% off!`);
    } catch (err) {
      setPromo(null);
      toast.error(err.response?.data?.detail || "Invalid promo code");
    } finally { setPromoBusy(false); }
  };
  const removePromo = () => { setPromo(null); setPromoInput(""); };

  const applyVoucher = async () => {
    const code = voucherInput.trim();
    if (!code) return;
    setVoucherBusy(true);
    try {
      const r = await api.post("/vouchers/validate", { code });
      setVoucher({ code: r.data.code, balance: r.data.balance, currency: r.data.currency || "USD" });
      const symb = (r.data.currency || "USD") === "JMD" ? "J$" : "$";
      toast.success(`Voucher "${r.data.code}" applied. ${symb}${r.data.balance.toFixed(2)} ${r.data.currency || "USD"} available!`);
    } catch (err) {
      setVoucher(null);
      toast.error(err.response?.data?.detail || "Invalid voucher code");
    } finally { setVoucherBusy(false); }
  };
  const removeVoucher = () => { setVoucher(null); setVoucherInput(""); };

  if (loading) return <div className="p-10 text-center">Loading...</div>;
  if (items.length === 0) return <Navigate to="/cart" replace />;

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="checkout-auth-wall">
        <div className="c4t-card">
          <h1 className="text-3xl font-['Fredoka'] font-bold">Sign in to checkout</h1>
          <p className="text-[#455A64] mt-2">Please sign in to place your order. We'll also save your order history.</p>
          <Link to="/login"
            onClick={() => localStorage.setItem("c4t_after_login", "/checkout")}
            className="c4t-btn c4t-btn-pink w-full mt-6 !no-underline" data-testid="checkout-login-btn">
            <LogIn className="w-5 h-5" /> Sign in or create account
          </Link>
          <Link to="/cart" className="block text-center mt-3 text-[#118AB2] hover:text-[#FF477E]">← Back to cart</Link>
        </div>
      </div>
    );
  }

  const update = (k, v) => setForm({ ...form, [k]: v });

  const handleProof = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Image must be under 4MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setProof(reader.result);
    reader.readAsDataURL(file);
  };

  // ── Validate form fields shared by both payment flows ──
  const validateForm = () => {
    if (!form.customer_name || !form.email || !form.phone || !form.shipping_address) {
      toast.error("Please fill all required fields"); return false;
    }
    if (!shippingFieldsValid) {
      toast.error("Please fill all required shipping fields"); return false;
    }
    if (!agreedRefund) {
      toast.error("Please acknowledge the refund policy to continue"); return false;
    }
    return true;
  };

  // ── Bank transfer submit ──
  const submit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (!fullyCovered && !proof) {
      toast.error("Please upload proof of payment (bank transfer screenshot)"); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        payment_method: fullyCovered ? "store_credit" : "bank_transfer",
        paypal_order_id: "",
        items: items.map(i => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image })),
        payment_proof: fullyCovered ? "" : proof,
        subtotal, shipping_cost, tax: 0, total,
        discount: +(promoDiscount + bulkDiscount).toFixed(2),
        promo_code: promo?.code || "",
        voucher_code: voucher?.code || "",
        voucher_discount: voucherDiscount,
        store_credit_used: creditToUse,
        shipping_sub_option: shippingSubOption,
        shipping_fields: shippingFields,
        currency: "JMD", exchange_rate: exchangeRate || 160, totals_currency: "JMD",
      };
      const r = await api.post("/orders", payload);
      clearCart();
      nav(`/order-success?id=${r.data.id}`);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Please sign in to place your order");
        localStorage.setItem("c4t_after_login", "/checkout");
        nav("/login");
      } else {
        toast.error(err.response?.data?.detail || "Order failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fullyCovered = total === 0 && (creditToUse > 0 || voucherDiscount > 0);
  const canSubmit = !submitting && agreedRefund && shippingFieldsValid && (fullyCovered || !!proof || form.payment_method === 'wipay');
  const isWiPay = form.payment_method === "wipay" && !fullyCovered;

  // ── WiPay submit ──
  const submitWiPay = async () => {
    if (!validateForm()) return;
    setWipayProcessing(true);
    try {
      const payload = {
        ...form,
        payment_method: "wipay",
        paypal_order_id: "",
        items: items.map(i => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image })),
        payment_proof: "",
        subtotal, shipping_cost, tax: 0, total,
        discount: +(promoDiscount + bulkDiscount).toFixed(2),
        promo_code: promo?.code || "",
        voucher_code: voucher?.code || "",
        voucher_discount: voucherDiscount,
        store_credit_used: creditToUse,
        shipping_sub_option: shippingSubOption,
        shipping_fields: shippingFields,
        currency: "JMD", exchange_rate: exchangeRate || 160, totals_currency: "JMD",
      };
      const r = await api.post("/wipay/create-payment", payload);
      // Cart preserved — only cleared on /order-pending once payment confirmed
      window.location.href = r.data.wipay_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not initiate card payment. Please try again.");
      setWipayProcessing(false);
    }
  };
  const formReady = !!(form.customer_name && form.email && form.phone && form.shipping_address && shippingFieldsValid && agreedRefund);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="checkout-page">
      <h1 className="text-4xl sm:text-5xl font-['Fredoka'] font-bold mb-2">{t("checkout.title", "Checkout")}</h1>
      <p className="c4t-desc mb-8">Signed in as <strong>{user.email}</strong>. {t("checkout.signed_in_note", "A confirmation email will be sent after your order.")}</p>

      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Contact */}
          <div className="c4t-card">
            <h2 className="text-2xl font-['Fredoka'] font-bold mb-4">Contact & Shipping</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold">Full Name *</label>
                <input className="c4t-input mt-1" required value={form.customer_name} onChange={e => update("customer_name", e.target.value)} data-testid="checkout-name" />
              </div>
              <div>
                <label className="text-sm font-semibold">Email *</label>
                <input type="email" className="c4t-input mt-1" required value={form.email} onChange={e => update("email", e.target.value)} data-testid="checkout-email" />
              </div>
              <div>
                <label className="text-sm font-semibold">Phone *</label>
                <input className="c4t-input mt-1" required value={form.phone} onChange={e => update("phone", e.target.value)} data-testid="checkout-phone" />
              </div>
              <div>
                <label className="text-sm font-semibold">Country *</label>
                <select className="c4t-input mt-1" value={form.country} onChange={e => update("country", e.target.value)} data-testid="checkout-country">
                  {COUNTRIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-semibold">Shipping Address *</label>
                <textarea className="c4t-input mt-1" rows={3} required value={form.shipping_address} onChange={e => update("shipping_address", e.target.value)} placeholder="Street, City, Parish, Postal Code" data-testid="checkout-address" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-semibold">Order Notes (optional)</label>
                <textarea className="c4t-input mt-1" rows={2} value={form.notes} onChange={e => update("notes", e.target.value)} data-testid="checkout-notes" />
              </div>
            </div>
          </div>

          {/* Shipping option */}
          <div className="c4t-card">
            <h2 className="text-2xl font-['Fredoka'] font-bold mb-2 flex items-center gap-2"><Truck className="w-6 h-6 text-[#118AB2]" /> Shipping Option</h2>
            <p className="text-sm text-[#455A64] mb-3">{settings?.shipping_note}</p>
            <div className="space-y-2" data-testid="shipping-options">
              {shippingOptions.map(opt => (
                <label key={opt.name} className={`flex items-center justify-between gap-3 p-3 rounded-2xl border-2 cursor-pointer transition ${form.shipping_option === opt.name ? "border-[#FF477E] bg-[#FFF9F2]" : "border-[#073B4C]/20 bg-white"}`}
                  data-testid={`shipping-opt-${opt.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                  <span className="flex items-center gap-3">
                    <input type="radio" name="ship" value={opt.name} checked={form.shipping_option === opt.name}
                      onChange={() => changeShippingOption(opt.name)} className="accent-[#FF477E]" />
                    <span className="font-semibold">{opt.name}</span>
                  </span>
                  <span className="font-bold text-[#FF1F87]">
                    {opt.sub_options?.length
                      ? `from ${format(Math.min(...opt.sub_options.map(s => s.price)))}`
                      : opt.price > 0 ? format(opt.price)
                      : opt.name.toLowerCase().includes("international") ? "Quoted after order" : "Free"}
                  </span>
                </label>
              ))}
            </div>

            {selectedOpt.sub_options?.length > 0 && (
              <div className="mt-4">
                <label className="text-sm font-semibold">Select your location *</label>
                <select
                        value={shippingSubOption}
                        onChange={(e) => setShippingSubOption(e.target.value)}
                        className="w-full mt-2 rounded-2xl border-2 border-[#B845FF] bg-white px-4 py-3 text-base cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#FF1F87]"
                      >
                        <option value="">Choose your location...</option>
                        {selectedOpt.sub_options.map(sub => (
                          <option key={sub.location} value={sub.location}>{sub.location} ({format(sub.price)})</option>
                        ))}
                      </select>
              </div>
            )}

            {selectedOpt.fields?.length > 0 && (
              <div className="mt-4 space-y-3">
                {selectedOpt.fields.map(f => (
                  <div key={f.label}>
                    <label className="text-sm font-semibold">{f.label}{f.required ? " *" : ""}</label>
                    <input className="c4t-input mt-1" placeholder={f.placeholder || ""}
                      required={f.required}
                      value={shippingFields[f.label] || ""}
                      onChange={e => setShippingFields(prev => ({ ...prev, [f.label]: e.target.value }))} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Payment */}
          {fullyCovered ? (
            <div className="c4t-card" data-testid="payment-covered">
              <h2 className="text-2xl font-['Fredoka'] font-bold mb-3">Payment</h2>
              <div className="flex items-center gap-3 bg-[#F3E8FF] border-2 border-[#B845FF] rounded-2xl px-4 py-4">
                <Wallet className="w-6 h-6 text-[#B845FF] shrink-0" />
                <div>
                  <p className="font-bold text-[#B845FF]">No payment required</p>
                  <p className="text-xs text-[#455A64] mt-0.5">This order is fully covered by your store credit{voucher ? " and voucher" : ""}. No payment needed.</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="c4t-card">
              <h2 className="text-2xl font-['Fredoka'] font-bold mb-4">Payment Method</h2>

              {/* Payment method selector */}
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                <label className={`flex items-center gap-3 p-4 rounded-2xl border-2 cursor-pointer transition ${form.payment_method === "bank_transfer" ? "border-[#FF477E] bg-[#FFF9F2]" : "border-[#073B4C]/20 bg-white hover:border-[#073B4C]/40"}`}
                  data-testid="payment-bank_transfer">
                  <input type="radio" name="payment_method" value="bank_transfer"
                    checked={form.payment_method === "bank_transfer"}
                    onChange={() => update("payment_method", "bank_transfer")}
                    className="accent-[#FF477E]" />
                  <Banknote className="w-5 h-5 text-[#FF477E] shrink-0" />
                  <div>
                    <p className="font-semibold text-sm">Bank Transfer</p>
                    <p className="text-xs text-[#455A64]">Upload payment receipt</p>
                  </div>
                </label>

                <label className={`flex items-center gap-3 p-4 rounded-2xl border-2 cursor-pointer transition ${form.payment_method === "wipay" ? "border-[#00A651] bg-[#F0FFF6]" : "border-[#073B4C]/20 bg-white hover:border-[#073B4C]/40"}`}
                  data-testid="payment-wipay">
                  <input type="radio" name="payment_method" value="wipay"
                    checked={form.payment_method === "wipay"}
                    onChange={() => update("payment_method", "wipay")}
                    className="accent-[#00A651]" />
                  <CreditCard className="w-5 h-5 text-[#00A651] shrink-0" />
                  <div>
                    <p className="font-semibold text-sm">Credit / Debit Card</p>
                    <p className="text-xs text-[#455A64]">Visa, Mastercard. JMD cards accepted</p>
                  </div>
                </label>
              </div>

              {/* WiPay section */}
              {form.payment_method === "wipay" && (
                <div className="bg-[#F0FFF6] border-2 border-[#00A651]/40 rounded-2xl p-4 text-sm">
                  <p className="font-semibold text-[#00A651] flex items-center gap-2">
                    <CreditCard className="w-4 h-4" /> Pay securely with your credit or debit card
                  </p>
                  <p className="text-[#455A64] mt-1">You'll be redirected to WiPay's secure payment page. Visa and Mastercard accepted. A small processing fee is added by WiPay at checkout.</p>
                  {currency === "USD" && (
                    <p className="text-amber-700 mt-2 text-xs font-semibold">Your USD total will be converted to JMD at the current exchange rate for card processing.</p>
                  )}
                </div>
              )}

              {/* Bank transfer details */}
              {form.payment_method === "bank_transfer" && (
                <div className="space-y-4">
                  <div className="c4t-card !bg-[#FFF9F2] !shadow-[4px_4px_0_#073B4C]">
                    <h3 className="font-bold mb-2 flex items-center gap-2"><Banknote className="w-5 h-5" /> Bank Payment Instructions</h3>
                    <pre className="whitespace-pre-wrap font-['Nunito'] text-[#455A64]">{settings?.bank_instructions}</pre>
                  </div>
                  <div>
                    <label className="text-sm font-semibold">Upload Proof of Payment *</label>
                    <label className="mt-2 flex items-center justify-center gap-2 c4t-pill cursor-pointer w-full !py-6 border-dashed">
                      <Upload className="w-5 h-5" /> {proof ? "Change file" : "Click to upload screenshot"}
                      <input type="file" accept="image/*" className="hidden" onChange={handleProof} data-testid="proof-upload" />
                    </label>
                    {proof && <img src={proof} alt="proof" className="mt-3 max-h-48 rounded-2xl border-2 border-[#073B4C]" data-testid="proof-preview" />}
                  </div>
                </div>
              )}

            </div>
          )}
        </div>

        {/* Summary sidebar */}
        <div className="c4t-card h-fit sticky top-24" data-testid="checkout-summary">
          <h2 className="text-2xl font-['Fredoka'] font-bold mb-1">Order Summary</h2>
          <p className="text-xs c4t-desc mb-3">All prices are in the currency you selected</p>
          <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
            {items.map(i => (
              <div key={i.product_id} className="flex justify-between text-sm">
                <span className="c4t-desc">{i.quantity} × {i.name}</span>
                <span className="font-bold">{format(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t-2 border-dashed border-black/30 pt-3 space-y-2">
            <div className="flex justify-between"><span className="c4t-desc">Subtotal</span><span className="font-bold" data-testid="summary-subtotal">{format(subtotal)}</span></div>
            {promo && (
              <div className="flex justify-between text-[#00A86B]" data-testid="summary-discount-row">
                <span className="font-semibold">Promo ({promo.code} −{promo.percent_off}%)</span>
                <span className="font-bold">−{format(promoDiscount)}</span>
              </div>
            )}
              {bulkDiscount > 0 && (
              <div className="flex justify-between text-[#00A86B]" data-testid="summary-discount-row">
                <span className="font-semibold">({"Bulk discount"} −{(settings?.bulk_discount_percent || 0)}%)</span>
                <span className="font-bold">−{format(bulkDiscount)}</span>
              </div>
            )}
            <div className="flex justify-between"><span className="c4t-desc">Shipping {shippingSubOption ? `(${shippingSubOption})` : `(${form.shipping_option})`}</span><span className="font-bold" data-testid="summary-shipping">{format(shipping_cost)}</span></div>
            {voucher && voucherDiscount > 0 && (
              <div className="flex justify-between text-[#00A6E0]" data-testid="summary-voucher-row">
                <span className="font-semibold flex items-center gap-1"><Gift className="w-3 h-3" /> Voucher ({voucher.code})</span>
                <span className="font-bold">−{format(voucherDiscount)}</span>
              </div>
            )}
            {creditToUse > 0 && (
              <div className="flex justify-between text-[#B845FF]" data-testid="summary-credit-row">
                <span className="font-semibold flex items-center gap-1"><Wallet className="w-3 h-3" /> Store Credit</span>
                <span className="font-bold">−{format(creditToUse)}</span>
              </div>
            )}
            <div className="flex justify-between text-xl font-['Fredoka'] font-bold pt-2 border-t-2 border-black/20">
              <span>Total</span><span className="text-[#FF1F87]" data-testid="checkout-total">{format(total)}</span>
            </div>
          </div>

          {/* Promo code */}
          <div className="mt-4 pt-4 border-t-2 border-dashed border-black/30">
            {promo ? (
              <div className="flex items-center justify-between bg-[#E6FFF7] border-2 border-[#00A86B] rounded-2xl px-3 py-2" data-testid="promo-applied-pill">
                <span className="text-sm font-bold text-[#00A86B] flex items-center gap-2"><Tag className="w-4 h-4" /> {promo.code} ({promo.percent_off}% off)</span>
                <button type="button" onClick={removePromo} className="text-[#455A64] hover:text-[#FF1F87]"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input type="text" value={promoInput} onChange={e => setPromoInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && (e.preventDefault(), applyPromo())}
                  placeholder="Promo code" className="c4t-input flex-1 uppercase" data-testid="promo-input" />
                <button type="button" onClick={applyPromo} disabled={promoBusy || !promoInput.trim()}
                  className="c4t-btn c4t-btn-yellow !px-4 !py-2 !text-sm" data-testid="promo-apply-btn">
                  <Tag className="w-4 h-4" /> {promoBusy ? "..." : "Apply"}
                </button>
              </div>
            )}
          </div>

          {/* Voucher code */}
          <div className="mt-3">
            {voucher ? (
              <div className="flex items-center justify-between bg-[#E6F7FF] border-2 border-[#00A6E0] rounded-2xl px-3 py-2" data-testid="voucher-applied-pill">
                <span className="text-sm font-bold text-[#00A6E0] flex items-center gap-2"><Gift className="w-4 h-4" /> {voucher.code} ({voucher.currency === "JMD" ? "J$" : "$"}{(voucher.balance || 0).toFixed(2)} {voucher.currency || "USD"} balance)</span>
                <button type="button" onClick={removeVoucher} className="text-[#455A64] hover:text-[#FF1F87]"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input type="text" value={voucherInput} onChange={e => setVoucherInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && (e.preventDefault(), applyVoucher())}
                  placeholder="Voucher / gift card code" className="c4t-input flex-1 uppercase" data-testid="voucher-input" />
                <button type="button" onClick={applyVoucher} disabled={voucherBusy || !voucherInput.trim()}
                  className="c4t-btn !bg-[#00E0AA] !text-black !px-4 !py-2 !text-sm" data-testid="voucher-apply-btn">
                  <Gift className="w-4 h-4" /> {voucherBusy ? "..." : "Apply"}
                </button>
              </div>
            )}
          </div>

          {/* Store credit notice */}
          {storeCredit.amount > 0 && (
            <div className="mt-3 bg-[#F3E8FF] border-2 border-[#B845FF] rounded-2xl px-3 py-2 text-sm" data-testid="store-credit-notice">
              <p className="font-bold text-[#B845FF] flex items-center gap-2"><Wallet className="w-4 h-4" /> Store Credit Applied</p>
              <p className="text-[#455A64] text-xs mt-0.5">
                J${Math.round(creditJMD).toLocaleString()} JMD available. <strong>{format(creditToUse)}</strong> will be deducted.
                {creditRemainingJMD > 0.5 ? ` J$${Math.round(creditRemainingJMD).toLocaleString()} JMD will remain on your account.` : ""}
              </p>
            </div>
          )}

          {/* Refund policy */}
          <label className="flex items-start gap-3 mt-5 cursor-pointer" data-testid="refund-policy-label">
            <input type="checkbox" checked={agreedRefund} onChange={e => setAgreedRefund(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#FF477E] shrink-0" data-testid="refund-policy-checkbox" />
            <span className="text-xs text-[#455A64] leading-relaxed">
              I acknowledge that <strong className="text-black">all sales are final</strong>. No refunds will be processed. By placing this order I accept Cheap4Teach's sales policy. For assistance, please contact us directly.
            </span>
          </label>

          {/* Place Order button — bank transfer and store credit only */}
          {!isWiPay && (
            <button type="submit" disabled={!canSubmit} className="mt-3 c4t-btn c4t-btn-pink w-full disabled:opacity-50 disabled:cursor-not-allowed" data-testid="place-order-btn">
              {submitting ? "Placing order..." : fullyCovered ? "Place Order (Free)" : "Place Order"}
            </button>
          )}

          {/* WiPay card payment button */}
          {isWiPay && (
            <button
              type="button"
              onClick={submitWiPay}
              disabled={wipayProcessing || !formReady}
              className="mt-3 w-full py-3 px-6 rounded-2xl font-bold text-white border-[3px] border-black shadow-[4px_4px_0_#000] transition-all hover:translate-y-[2px] hover:shadow-[2px_2px_0_#000] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ background: wipayProcessing ? "#6b7280" : "#00A651" }}
              data-testid="wipay-pay-btn"
            >
              <CreditCard className="w-5 h-5" />
              {wipayProcessing ? "Redirecting to payment..." : "Pay by Card (WiPay)"}
            </button>
          )}

          <p className="text-xs text-[#455A64] mt-3 flex items-center gap-2 justify-center">
            <ShieldCheck className="w-4 h-4 text-[#06D6A0]" /> Secure checkout
          </p>
          <Link to="/cart" className="block text-center mt-2 text-[#118AB2] hover:text-[#FF477E] text-sm">← Back to cart</Link>
        </div>
      </form>
    </div>
  );
}
